import type { ReactNode } from 'react';
import type { DensityLevel } from './layout/Density';

/**
 * 🔴 THE QUESTIONS WAITING FOR AN ANSWER — design-system plan DS-8 (5.1: `Dialog`, `useConfirm`, `usePrompt`; F496,
 * F39, F210).
 *
 * `window.confirm` and `window.prompt` were easy to call because they were a function that returned the answer. This
 * keeps that shape: `confirmDialog({ … })` resolves `true` or `false` and `promptDialog({ … })` the typed text or
 * `null`, from anywhere — a click handler, a till's scan handler, a router blocker — and ONE `DialogHost` per shell
 * draws the question, the way one `Toaster` draws the toasts. A question asked while another is open waits its turn.
 *
 * ⚠️ A SHELL WITHOUT A HOST WOULD LEAVE THE PROMISE WAITING FOR EVER — the screen would sit on "Void invoice" with
 * nothing drawn and nothing said. So a question asked with no host mounted is REFUSED (the promise rejects), which a
 * spec and the error boundary both see. The three roots (web/src/main.tsx, web/portal/src/main.tsx,
 * web/system/src/main.tsx) and the spec harness's `WithFeedback` mount it.
 */
export type DialogTone = 'default' | 'danger';

/** Content pattern: `title` is verb + object ("Void invoice INV-0042?"), `body` the consequence in a sentence. */
export interface ConfirmOptions {
  title: string;
  body?: ReactNode;
  /** Repeats the title's verb: "Void invoice", never "OK" or "Yes". */
  confirmLabel: string;
  /** The way back. "Cancel" unless the verb is itself cancel: then "Keep order", "Go back". */
  cancelLabel?: string;
  /** `danger` draws the one filled danger control (O-27) and puts focus on the way back, not on the verb. */
  tone?: DialogTone;
}

/**
 * What the prompt asks for. `amount` reads money as a person writes it (Bengali digits, lakh or western commas, a
 * leading ৳/Tk/BDT, a trailing "/-"), `quantity` a decimal the same way, `reason` is several lines of text, `text` one.
 */
export type PromptKind = 'text' | 'amount' | 'quantity' | 'reason';

export interface PromptOptions {
  title: string;
  body?: ReactNode;
  /** The field's label. */
  label: string;
  kind?: PromptKind;
  /** What the field starts with, such as the cheque's date. */
  initial?: string;
  hint?: string;
  /** Default true: an empty answer is refused with "Enter the <label>." */
  required?: boolean;
  /**
   * The screen's own rule, run on the READ value (for `amount` and `quantity` the canonical decimal, "105000.5"; for
   * text the trimmed text). Return the sentence that says what is wrong, or nothing.
   */
  validate?: (value: string) => string | null | undefined;
  confirmLabel: string;
  cancelLabel?: string;
  tone?: DialogTone;
  /** The till's on-screen keypad under the field, for `amount` and `quantity`. */
  keypad?: boolean;
}

/**
 * Where a question was asked from (DS-9a). The dialog is drawn into `<body>` by the root's host, outside the subtree
 * whose button asked it, so the subtree's density comes with the question: `useConfirm` and `usePrompt` pass it, and
 * the dialog carries it as `data-density`. Asked from nowhere in particular, the dialog takes the page's own level.
 */
export interface AskedFrom {
  density?: DensityLevel;
}

export type DialogRequest =
  | { id: number; type: 'confirm'; options: ConfirmOptions; density?: DensityLevel; settle: (answer: boolean) => void }
  | { id: number; type: 'prompt'; options: PromptOptions; density?: DensityLevel; settle: (answer: string | null) => void };

export interface DialogSnapshot {
  /** The questions in the order they were asked; the host draws the first. */
  queue: readonly DialogRequest[];
  /** The one host that draws them: the first mounted. A second host (a spec wrapping a shell) draws nothing. */
  host: object | null;
}

export const NO_HOST =
  'No DialogHost is mounted, so nothing could draw this question. Mount <DialogHost /> beside the Toaster (the three ' +
  'app roots and the spec harness do).';

/**
 * ⚠️ A PROMPT NEEDS A HOST THAT WAS GIVEN THE PROMPT DIALOG. The field, the number reading and the till's keypad are
 * weight the portal and the console never use, so their hosts draw confirms only and their first load does not carry
 * the rest; the back office's host (which serves the till, whose prompts must work offline) is handed `PromptDialog`.
 * A prompt asked in a shell that cannot draw it is refused at once, not left waiting.
 */
export const NO_PROMPTS =
  "This shell's DialogHost draws no prompts. Give it the prompt dialog: <DialogHost prompt={PromptDialog} /> " +
  '(web/src/main.tsx and the spec harness do).';

interface Host {
  host: object;
  prompts: boolean;
}

let snapshot: DialogSnapshot = { queue: [], host: null };
let hosts: Host[] = [];
let next = 1;
const listeners = new Set<() => void>();

function publish(queue: readonly DialogRequest[]): void {
  snapshot = { queue, host: hosts[0]?.host ?? null };
  for (const l of listeners) l();
}

function ask<T>(prompt: boolean, make: (id: number, settle: (answer: T) => void) => DialogRequest): Promise<T> {
  const active = hosts[0];
  if (!active) return Promise.reject(new Error(NO_HOST));
  if (prompt && !active.prompts) return Promise.reject(new Error(NO_PROMPTS));
  return new Promise<T>((resolve) => {
    const id = next++;
    let settled = false;
    // Once only: Escape and a click on Cancel can both arrive for the same question.
    const settle = (answer: T) => {
      if (settled) return;
      settled = true;
      publish(snapshot.queue.filter((r) => r.id !== id));
      resolve(answer);
    };
    publish([...snapshot.queue, make(id, settle)]);
  });
}

/** Ask a yes-or-no question: resolves true when the person confirms, false when they cancel or press Escape. */
export function confirmDialog(options: ConfirmOptions, from: AskedFrom = {}): Promise<boolean> {
  return ask<boolean>(false, (id, settle) => ({ id, type: 'confirm', options, density: from.density, settle }));
}

/** Ask for a value: resolves the read value (see `PromptOptions.validate`), or null when the person cancels. */
export function promptDialog(options: PromptOptions, from: AskedFrom = {}): Promise<string | null> {
  return ask<string | null>(true, (id, settle) => ({ id, type: 'prompt', options, density: from.density, settle }));
}

export function subscribeDialogs(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function dialogSnapshot(): DialogSnapshot {
  return snapshot;
}

/** A host says it is there, and whether it can draw a prompt; the returned function says it has gone. */
export function registerHost(host: object, { prompts }: { prompts: boolean }): () => void {
  const entry: Host = { host, prompts };
  hosts = [...hosts, entry];
  publish(snapshot.queue);
  return () => {
    hosts = hosts.filter((h) => h !== entry);
    publish(snapshot.queue);
  };
}

/** For specs: answer every open question with its cancel (false, null) and forget them. */
export function resetDialogs(): void {
  for (const r of snapshot.queue) {
    if (r.type === 'confirm') r.settle(false);
    else r.settle(null);
  }
  publish([]);
}
